import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RegisterForm from '../components/auth/RegisterForm';
import Logo from '../components/ui/Logo';
import ConfirmationModal from '../components/ui/ConfirmationModal';
import { useAuth } from '../hooks/useAuth';

const RegisterPage: React.FC = () => {
  const { register, loading } = useAuth();
  const navigate = useNavigate();
  const [userType, setUserType] = useState<'secretary' | 'supervisor'>('secretary');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleRegister = async (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
    institutionId?: string;
  }) => {
    try {
      setError(null);
      
      // Validation supplémentaire
      if (userType === 'secretary' && !data.institutionId?.trim()) {
        throw new Error('L\'identifiant d\'institution est requis pour les secrétaires');
      }

      const displayName = `${data.firstName} ${data.lastName}`;
      
      // Utiliser la nouvelle signature avec additionalData
      await register(
        data.email,
        data.password,
        displayName,
        userType,
        data.institutionId,
        {
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone
        }
      );
      
      setRegisteredEmail(data.email);
      setRegistrationSuccess(true);
      
    } catch (error: any) {
      console.error('Registration error:', error);
      setError(error.message || 'Une erreur est survenue lors de l\'inscription');
      throw error;
    }
  };

  const handleCloseConfirmation = () => {
    setRegistrationSuccess(false);
    navigate('/login');
  };

  if (registrationSuccess) {
    return (
      <ConfirmationModal
        email={registeredEmail}
        userType={userType}
        onClose={handleCloseConfirmation}
        autoRedirect={true}
        redirectDelay={5000}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Logo size="xl" showText={false} />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Créer un nouveau compte
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Déjà inscrit ?{' '}
          <a 
            href="/login" 
            className="font-semibold text-blue-600 hover:text-blue-500 transition-colors"
          >
            Connectez-vous ici
          </a>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-lg sm:rounded-xl sm:px-10 border border-gray-200">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm font-medium">
                {error}
              </p>
            </div>
          )}
          
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Type de compte
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setUserType('secretary')}
                className={`py-3 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                  userType === 'secretary'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow'
                }`}
              >
                <div className="flex flex-col items-center">
                  <span className="font-semibold">Secrétaire</span>
                  <span className="text-xs mt-1 opacity-80">
                    Gestion des transactions
                  </span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setUserType('supervisor')}
                className={`py-3 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                  userType === 'supervisor'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow'
                }`}
              >
                <div className="flex flex-col items-center">
                  <span className="font-semibold">Superviseur</span>
                  <span className="text-xs mt-1 opacity-80">
                    Supervision des institutions
                  </span>
                </div>
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              {userType === 'secretary' 
                ? 'Pour les employés d\'institution financière'
                : 'Pour les administrateurs et superviseurs'
              }
            </p>
          </div>

          <RegisterForm 
            onSubmit={handleRegister} 
            isLoading={loading}
            userType={userType}
          />
          
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              En vous inscrivant, vous acceptez nos{' '}
              <a href="/terms" className="text-blue-600 hover:text-blue-800 font-medium">
                Conditions d'utilisation
              </a>{' '}
              et notre{' '}
              <a href="/privacy" className="text-blue-600 hover:text-blue-800 font-medium">
                Politique de confidentialité
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;